/**
 * The thin-lens and mirror equation, 1/f = 1/dₒ + 1/dᵢ, with the "real is positive"
 * sign convention used in most introductory courses:
 *
 * - f is positive for a converging element (convex lens, concave mirror) and negative
 *   for a diverging one (concave lens, convex mirror).
 * - dₒ is positive for a real object in front of the element.
 * - dᵢ is positive for a real image (on the far side of a lens, in front of a mirror)
 *   and negative for a virtual image.
 * - m = −dᵢ ÷ dₒ is negative for an inverted image; hᵢ = m × hₒ.
 *
 * Lengths are in any one consistent unit; the functions do not convert.
 */

export type OpticsKind = "lens" | "mirror";
export type OpticsTarget = "image" | "focal" | "object";

export type OpticsError =
  | "need-positive-object"
  | "at-focal-point"
  | "zero-denominator"
  | "invalid";

export interface OpticsSolution {
  /** Focal length, signed. */
  f: number;
  /** Object distance, positive. */
  dObject: number;
  /** Image distance, signed (negative for a virtual image). */
  dImage: number;
  magnification: number;
}

export type OpticsResult =
  | { ok: true; solution: OpticsSolution }
  | { ok: false; error: OpticsError };

const EPSILON = 1e-12;

function finish(f: number, dObject: number, dImage: number): OpticsResult {
  if (
    !(Number.isFinite(f) && Number.isFinite(dImage) && Number.isFinite(dObject))
  ) {
    return { ok: false, error: "invalid" };
  }
  if (!(dObject > 0)) {
    return { ok: false, error: "need-positive-object" };
  }
  return {
    ok: true,
    solution: { f, dObject, dImage, magnification: -dImage / dObject },
  };
}

/**
 * Solve for the image distance, the focal length or the object distance. `known` holds
 * the other two (signed f, positive dₒ, signed dᵢ). Reports the cases with no finite
 * answer: an object at the focal point throws the image to infinity.
 */
export function solveOptics(
  target: OpticsTarget,
  known: { f?: number; dObject?: number; dImage?: number }
): OpticsResult {
  const { f, dObject, dImage } = known;
  if (target === "image") {
    if (f === undefined || dObject === undefined || f === 0) {
      return { ok: false, error: "invalid" };
    }
    if (!(dObject > 0)) {
      return { ok: false, error: "need-positive-object" };
    }
    if (Math.abs(dObject - f) < EPSILON * Math.abs(f)) {
      return { ok: false, error: "at-focal-point" };
    }
    return finish(f, dObject, (f * dObject) / (dObject - f));
  }
  if (target === "focal") {
    if (dObject === undefined || dImage === undefined || dImage === 0) {
      return { ok: false, error: "invalid" };
    }
    if (Math.abs(dObject + dImage) < EPSILON * dObject) {
      return { ok: false, error: "zero-denominator" };
    }
    return finish((dObject * dImage) / (dObject + dImage), dObject, dImage);
  }
  if (f === undefined || dImage === undefined || f === 0 || dImage === 0) {
    return { ok: false, error: "invalid" };
  }
  if (Math.abs(dImage - f) < EPSILON * Math.abs(f)) {
    return { ok: false, error: "zero-denominator" };
  }
  return finish(f, (f * dImage) / (dImage - f), dImage);
}

export interface ImageNature {
  real: boolean;
  upright: boolean;
  /** "magnified", "reduced" or "same" size. */
  size: "magnified" | "reduced" | "same";
}

export function imageNature(solution: OpticsSolution): ImageNature {
  const m = solution.magnification;
  const abs = Math.abs(m);
  let size: ImageNature["size"] = "same";
  if (Math.abs(abs - 1) > 1e-9) {
    size = abs > 1 ? "magnified" : "reduced";
  }
  return { real: solution.dImage > 0, upright: m > 0, size };
}

/** Optical power 1/f in dioptres, from a focal length in metres. */
export function opticalPower(focalMetres: number): number {
  return 1 / focalMetres;
}

export interface RaySegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  dashed: boolean;
  /** Which principal ray: 1 parallel, 2 central, 3 through the focal point. */
  ray: 1 | 2 | 3;
}

export interface RayModel {
  segments: RaySegment[];
  /** Object tip and image tip positions, in the diagram's coordinates (x along the axis). */
  object: { x: number; y: number };
  image: { x: number; y: number };
  /** Focal point positions on the axis. */
  focalPoints: number[];
  /** Horizontal extent the drawing needs, left and right of the element. */
  extent: { left: number; right: number };
}

/**
 * Geometry of the principal rays for a lens or mirror. Coordinates: the element sits at
 * x = 0, the object on the left at x = −dₒ with its tip at height hₒ; a lens passes
 * light to the right, a mirror sends it back to the left. `reach` is how far each ray
 * is drawn beyond the element.
 */
export function rayModel(
  kind: OpticsKind,
  solution: OpticsSolution,
  objectHeight: number,
  reach: number
): RayModel {
  const { f, dObject, dImage } = solution;
  const h = objectHeight;
  const hi = solution.magnification * h;
  const lens = kind === "lens";
  const dir = lens ? 1 : -1;
  const imageX = lens ? dImage : -dImage;
  const focalPoints = lens ? [f, -f] : [-f];
  const segments: RaySegment[] = [];
  const add = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    ray: 1 | 2 | 3,
    dashed = false
  ) => segments.push({ x1, y1, x2, y2, dashed, ray });

  // Ray 1: parallel to the axis, then through (or away from) the focal point.
  add(-dObject, h, 0, h, 1);
  const slope1 = lens ? -h / f : h / f;
  add(0, h, dir * reach, h + slope1 * dir * reach, 1);
  if (f < 0) {
    add(0, h, lens ? f : -f, 0, 1, true);
  }

  // Ray 2: through the centre of the lens, or reflected at the vertex of the mirror.
  add(-dObject, h, 0, 0, 2);
  const slope2 = lens ? -h / dObject : h / dObject;
  add(0, 0, dir * reach, slope2 * dir * reach, 2);

  // Ray 3: through the near focal point, then parallel to the axis.
  if (Math.abs(dObject - f) > 1e-9 * Math.abs(f)) {
    const y3 = (h * f) / (f - dObject);
    add(-dObject, h, 0, y3, 3);
    add(0, y3, dir * reach, y3, 3);
  }

  const virtual = dImage < 0;
  if (virtual) {
    add(0, h, imageX, hi, 1, true);
    add(0, 0, imageX, hi, 2, true);
  }

  const xs = [-dObject, imageX, ...focalPoints, 0];
  return {
    segments,
    object: { x: -dObject, y: h },
    image: { x: imageX, y: hi },
    focalPoints,
    extent: { left: Math.min(...xs), right: Math.max(...xs) },
  };
}
